import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import schedulingApi from '../../api/scheduling';
import usersApi from '../../api/users';
import testHarness from '../../api/testHarness';

function getLocationLabel(schedule, locById) {
  if (schedule?.location?.name) return schedule.location.name;
  if (schedule?.location && typeof schedule.location === 'number') {
    return locById[schedule.location] || `Location ${schedule.location}`;
  }
  return 'Unknown location';
}

function getPeriodLabel(schedule, perById) {
  if (schedule?.period?.start_date && schedule?.period?.end_date) {
    return `${schedule.period.start_date} → ${schedule.period.end_date}`;
  }
  if (schedule?.period && typeof schedule.period === 'number') {
    return perById[schedule.period] || `Period ${schedule.period}`;
  }
  return 'Unknown period';
}

function scheduleSortValue(schedule) {
  const periodStart = schedule?.period?.start_date;
  if (periodStart) return new Date(periodStart).getTime();
  return 0;
}

/**
 * Shared modal for swap simulation — picks a target schedule, probability,
 * and RNG seed. `mode` is 'posts' or 'claims', which picks the backend call.
 */
export default function SimulateSwapsModal({ mode, onClose, onDone }) {
  const [scheduleId, setScheduleId] = useState('');
  const [probability, setProbability] = useState(mode === 'posts' ? 0.25 : 0.5);
  const [seed, setSeed]             = useState('');
  const [error, setError]           = useState('');

  const { data: schedules = [] } = useQuery({
    queryKey: ['schedules'],
    queryFn:  schedulingApi.getSchedules,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn:  usersApi.getLocations,
  });
  const { data: periods = [] } = useQuery({
    queryKey: ['periods'],
    queryFn:  schedulingApi.getPeriods,
  });

  const locById = Object.fromEntries(locations.map((l) => [l.id, l.name]));
  const perById = Object.fromEntries(periods.map((p) => [p.id, `${p.start_date} → ${p.end_date}`]));

  // Swap activity can only happen on PUBLISHED schedules
  const eligible = schedules
    .filter((s) => s.status === 'PUBLISHED')
    .sort((a, b) => scheduleSortValue(b) - scheduleSortValue(a));

  const mutation = useMutation({
    mutationFn: (body) =>
      mode === 'posts'
        ? testHarness.simulateSwapPosts(body)
        : testHarness.simulateSwapClaims(body),
    onSuccess: (data) => { onDone?.(data); onClose(); },
    onError:   (err)  => setError(err.data?.detail || err.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!scheduleId) { setError('Pick a schedule.'); return; }
    const body = {
      schedule_id: parseInt(scheduleId, 10),
      probability,
    };
    if (seed !== '') body.rng_seed = parseInt(seed, 10);
    mutation.mutate(body);
  };

  const title = mode === 'posts' ? 'Simulate Swap Posts' : 'Simulate Swap Claims';
  const description = mode === 'posts'
    ? 'Mock SLs randomly post their upcoming shifts to the marketplace.'
    : 'Mock SLs randomly claim currently-open listings (enforces female-for-female on Fire).';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl space-y-4"
      >
        <div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Target schedule</label>
          <select
            value={scheduleId}
            onChange={(e) => setScheduleId(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">— Select a published schedule —</option>
            {eligible.map((s) => (
              <option key={s.id} value={s.id}>
                {getLocationLabel(s, locById)} · {getPeriodLabel(s, perById)}
              </option>
            ))}
          </select>
          {!eligible.length && (
            <p className="mt-1 text-[11px] text-gray-500">No PUBLISHED schedules yet.</p>
          )}
        </div>

        <div>
          <div className="flex justify-between text-xs font-medium text-gray-700 mb-1">
            <span>Probability per shift</span>
            <span>{probability.toFixed(2)}</span>
          </div>
          <input
            type="range"
            min="0" max="1" step="0.05"
            value={probability}
            onChange={(e) => setProbability(parseFloat(e.target.value))}
            className="w-full accent-indigo-600"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">RNG seed (optional)</label>
          <input
            type="number"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="e.g. 42"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={mutation.isPending || !eligible.length}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
            {mutation.isPending ? 'Running…' : 'Run Simulation'}
          </button>
        </div>
      </form>
    </div>
  );
}
